import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Search, MoreHorizontal, Pencil, Eye, UserX, UserCheck, Trash2, AlertTriangle, KeyRound, Flame } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatApiError } from "../lib/format";
import { ROLE_LABEL, DIVISIONS, canDelete, canForceDelete, isManagement } from "../lib/constants";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import ForceDeleteModal from "../components/ForceDeleteModal";

const ROLES = [
  "owner","admin","general_manager","division_manager","division_admin","division_staff","content_creator"
];

const emptyEmp = {
  email: "", name: "", role: "division_staff", division_id: "",
  phone: "", employee_id: "", position: "", join_date: "",
  bank_name: "", bank_account_number: "", bank_account_holder: "",
  npwp: "", address: "", notes: "", status: "active", create_login: false, password: "",
};

export default function Employees() {
  const { user } = useAuth();
  const mgmt = isManagement(user);
  const owner = canDelete(user);
  const canForce = canForceDelete(user);

  const [rows, setRows] = useState([]);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmp);
  const [busy, setBusy] = useState(false);
  const [initialPassword, setInitialPassword] = useState(null);

  const [detail, setDetail] = useState(null);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConflict, setDeleteConflict] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", { params: { status: tab === "inactive" ? "inactive" : tab === "all" ? "all" : "active" } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.employee_id || "").toLowerCase().includes(s) ||
      (r.position || "").toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s);
  }), [rows, q]);

  const openCreate = () => { setEditing(null); setForm(emptyEmp); setInitialPassword(null); setFormOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      email: e.email || "", name: e.name || "",
      role: e.role || "division_staff", division_id: e.division_id || "",
      phone: e.phone || "", employee_id: e.employee_id || "",
      position: e.position || "", join_date: e.join_date || "",
      bank_name: e.bank_name || "", bank_account_number: e.bank_account_number || "",
      bank_account_holder: e.bank_account_holder || "", npwp: e.npwp || "",
      address: e.address || "", notes: e.notes || "", status: e.status || "active",
      create_login: false, password: "",
    });
    setInitialPassword(null);
    setFormOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nama wajib diisi"); return; }
    if (!editing && !form.email.trim()) { toast.error("Email wajib diisi"); return; }
    setBusy(true);
    try {
      if (editing) {
        const { email, create_login, password, ...body } = form;
        // eslint-disable-next-line no-unused-vars
        const _ = { email, create_login, password };
        await api.put(`/employees/${editing.id}`, body);
        toast.success("Karyawan diperbarui");
        setFormOpen(false); load();
      } else {
        const { data } = await api.post("/employees", form);
        if (data?._initial_password) {
          setInitialPassword({ email: data.email, password: data._initial_password });
          toast.success("Karyawan ditambahkan dengan akun login");
        } else {
          toast.success("Karyawan ditambahkan (tanpa akses login)");
          setFormOpen(false);
        }
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openDetail = async (e) => {
    try {
      const { data } = await api.get(`/employees/${e.id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDeactivate = async () => {
    if (!deactivateTarget) return;
    setBusy(true);
    try {
      await api.post(`/employees/${deactivateTarget.id}/deactivate`, { reason: deactivateReason.trim() });
      toast.success("Karyawan dinonaktifkan");
      setDeactivateTarget(null); setDeactivateReason(""); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doActivate = async (e) => {
    try {
      await api.post(`/employees/${e.id}/activate`);
      toast.success("Karyawan diaktifkan kembali");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDelete = async (e) => {
    try {
      const { data: rel } = await api.get(`/employees/${e.id}/relations`);
      if (rel.total > 0) {
        setDeleteConflict({ emp: e, relations: rel });
        return;
      }
      setDeleteTarget(e);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doDelete = async ({ reason }) => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/employees/${deleteTarget.id}`, { headers: reason ? { "X-Delete-Reason": reason } : {} });
      toast.success("Karyawan dihapus permanen");
      setDeleteTarget(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.relations) {
        setDeleteTarget(null);
        setDeleteConflict({ emp: deleteTarget, relations: d.relations });
      } else {
        toast.error(formatApiError(d));
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Master Karyawan</h1>
          <p className="text-sm text-[#5C6066] mt-1">Data pegawai perusahaan. Membuat pegawai tidak otomatis membuat akun login.</p>
        </div>
        {mgmt && (
          <Button className="bg-brand text-white" data-testid="btn-add-employee" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1.5" /> Tambah Pegawai
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab} data-testid="employees-tabs">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active">Aktif</TabsTrigger>
            <TabsTrigger value="inactive" data-testid="tab-inactive">Nonaktif</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Semua</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari nama, email, ID pegawai, jabatan..." value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="employee-search" />
        </div>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Nama</th>
                <th className="text-left px-4 py-3">Peran</th>
                <th className="text-left px-4 py-3">Divisi</th>
                <th className="text-left px-4 py-3">Jabatan</th>
                <th className="text-right px-4 py-3">Tugas</th>
                <th className="text-left px-4 py-3">Completion</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} className="text-center py-10 text-[#8C9096]">Memuat...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={8} className="text-center py-10 text-[#8C9096]">Belum ada karyawan.</td></tr>)}
              {!loading && filtered.map((u) => (
                <tr key={u.id} className={`border-t border-[#F0EBE0] hover:bg-[#F7F4EE] ${u.status === "inactive" ? "opacity-70" : ""}`} data-testid={`emp-row-${u.id}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => openDetail(u)} className="font-medium hover:text-brand text-left">{u.name}</button>
                    <div className="text-[11px] text-[#8C9096]">{u.employee_id ? `${u.employee_id} • ` : ""}{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#5C6066]">{ROLE_LABEL[u.role] || u.role}</td>
                  <td className="px-4 py-3 text-[#5C6066]">{u.division?.short || "-"}</td>
                  <td className="px-4 py-3 text-[#5C6066]">{u.position || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{u.stats?.tasks_total || 0}</td>
                  <td className="px-4 py-3 w-40">
                    <Progress value={u.stats?.completion_rate || 0} className="h-1.5" />
                    <div className="text-[10px] text-[#8C9096] mt-1">{u.stats?.completion_rate || 0}%</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={u.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <EmpActions u={u} me={user} mgmt={mgmt} owner={owner} canForce={canForce}
                      onView={() => openDetail(u)} onEdit={() => openEdit(u)}
                      onDeactivate={() => setDeactivateTarget(u)} onActivate={() => doActivate(u)}
                      onDelete={() => openDelete(u)}
                      onForceDelete={() => setForceDeleteTarget(u)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-[#F0EBE0]">
          {loading && <div className="text-center py-10 text-[#8C9096] text-sm">Memuat...</div>}
          {!loading && filtered.length === 0 && <div className="text-center py-10 text-[#8C9096] text-sm">Belum ada karyawan.</div>}
          {!loading && filtered.map((u) => (
            <div key={u.id} className="p-4 flex items-start justify-between gap-3" data-testid={`emp-card-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <button onClick={() => openDetail(u)} className="font-medium hover:text-brand text-left truncate">{u.name}</button>
                  <StatusPill status={u.status} />
                </div>
                <div className="text-[11px] text-[#8C9096] mt-0.5 truncate">
                  {ROLE_LABEL[u.role] || u.role} • {u.division?.short || "—"} • {u.position || "—"}
                </div>
              </div>
              <EmpActions u={u} me={user} mgmt={mgmt} owner={owner} canForce={canForce}
                onView={() => openDetail(u)} onEdit={() => openEdit(u)}
                onDeactivate={() => setDeactivateTarget(u)} onActivate={() => doActivate(u)}
                onDelete={() => openDelete(u)}
                onForceDelete={() => setForceDeleteTarget(u)} />
            </div>
          ))}
        </div>
      </Card>

      {/* Add / Edit Modal */}
      <Dialog open={formOpen} onOpenChange={(v) => { if (!v) { setFormOpen(false); setInitialPassword(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pegawai" : "Tambah Pegawai Baru"}</DialogTitle>
            <DialogDescription>
              {editing ? "Perbarui data pegawai. Ubah status di menu aksi." : "Data pegawai baru. Centang \"Buat akun login\" jika perlu diberikan akses aplikasi."}
            </DialogDescription>
          </DialogHeader>

          {initialPassword ? (
            <div className="space-y-3 py-3" data-testid="initial-password-box">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded">
                <div className="text-sm font-medium text-emerald-900 flex items-center gap-2"><KeyRound className="w-4 h-4"/> Akun login berhasil dibuat</div>
                <div className="text-xs text-emerald-800 mt-2">Bagikan kredensial berikut sekali ini saja:</div>
                <div className="mt-2 font-mono text-sm bg-white border border-emerald-200 rounded p-2">
                  <div>Email: <b>{initialPassword.email}</b></div>
                  <div>Password: <b>{initialPassword.password}</b></div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => { setFormOpen(false); setInitialPassword(null); }} className="bg-brand text-white">Tutup</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                <EField label="Nama Lengkap *" k="name" form={form} setForm={setForm}/>
                <EField label="Employee ID" k="employee_id" form={form} setForm={setForm} placeholder="EMP-001"/>
                <EField label="Email / Username *" k="email" form={form} setForm={setForm} disabled={!!editing}/>
                <EField label="WhatsApp / Phone" k="phone" form={form} setForm={setForm} placeholder="+62..."/>
                <div>
                  <Label>Divisi</Label>
                  <select className="w-full mt-1.5 h-10 rounded-md border border-[#E5E0D8] bg-white px-3 text-sm"
                    value={form.division_id} onChange={(e) => setForm({ ...form, division_id: e.target.value })} data-testid="emp-division_id">
                    <option value="">Tidak Ada</option>
                    {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <EField label="Jabatan" k="position" form={form} setForm={setForm} placeholder="Contoh: MUA Senior"/>
                <div>
                  <Label>Role Akses</Label>
                  <select className="w-full mt-1.5 h-10 rounded-md border border-[#E5E0D8] bg-white px-3 text-sm"
                    value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role">
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                  </select>
                </div>
                <EField label="Tanggal Masuk" k="join_date" type="date" form={form} setForm={setForm}/>
                <EField label="NPWP" k="npwp" form={form} setForm={setForm}/>
                <EField label="Bank" k="bank_name" form={form} setForm={setForm}/>
                <EField label="Nomor Rekening" k="bank_account_number" form={form} setForm={setForm}/>
                <EField label="Atas Nama" k="bank_account_holder" form={form} setForm={setForm}/>
                <div className="sm:col-span-2">
                  <Label>Alamat</Label>
                  <Textarea rows={2} className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="emp-address"/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Catatan</Label>
                  <Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="emp-notes"/>
                </div>

                {!editing && (
                  <div className="sm:col-span-2 border border-amber-200 bg-amber-50 rounded p-3">
                    <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                      <input type="checkbox" checked={form.create_login} onChange={(e) => setForm({ ...form, create_login: e.target.checked })} data-testid="emp-create-login"/>
                      <span><b>Buat akun login</b> — beri akses aplikasi ke pegawai ini</span>
                    </label>
                    {form.create_login && (
                      <div className="mt-3">
                        <Label className="text-xs">Password (kosongkan untuk default <code>welcome{new Date().getFullYear()}</code>)</Label>
                        <Input className="mt-1.5" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="emp-password"/>
                      </div>
                    )}
                    {!form.create_login && (
                      <div className="text-[11px] text-amber-800 mt-1">Pegawai akan tercatat di sistem tetapi <b>tidak dapat login</b>.</div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
                <Button onClick={submit} disabled={busy || !form.name.trim()} className="bg-brand text-white" data-testid="btn-save-employee">
                  {busy ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Simpan Pegawai"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="emp-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.name} <StatusPill status={detail.status} />
                </DialogTitle>
                <DialogDescription>{ROLE_LABEL[detail.role] || detail.role} • {detail.division?.name || "—"}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Email" value={detail.email} />
                <Info label="Phone" value={detail.phone} />
                <Info label="Employee ID" value={detail.employee_id} />
                <Info label="Jabatan" value={detail.position} />
                <Info label="Tanggal Masuk" value={detail.join_date ? formatDate(detail.join_date) : "—"} />
                <Info label="Bank" value={detail.bank_name ? `${detail.bank_name} — ${detail.bank_account_number}` : "—"} />
                {detail.address && <div className="col-span-2"><Info label="Alamat" value={detail.address} /></div>}
                {detail.notes && <div className="col-span-2"><Info label="Catatan" value={detail.notes} /></div>}
              </div>
              <div className="border-t border-[#F0EBE0] pt-3 mt-2 grid grid-cols-4 gap-2 text-center">
                <StatBox label="Total Tugas" value={detail.stats?.tasks_total} />
                <StatBox label="Selesai" value={detail.stats?.tasks_done} tone="success" />
                <StatBox label="Overdue" value={detail.stats?.tasks_overdue} tone="danger" />
                <StatBox label="Bukti" value={detail.stats?.evidence_uploads} />
              </div>
              <DialogFooter>
                <Link to={`/employees/${detail.id}`} className="text-sm text-brand hover:underline mr-auto">
                  Lihat Riwayat Lengkap →
                </Link>
                <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
                {mgmt && (
                  <Button onClick={() => { openEdit(detail); setDetail(null); }} className="bg-brand text-white">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirm */}
      <Dialog open={!!deactivateTarget} onOpenChange={(v) => !v && setDeactivateTarget(null)}>
        <DialogContent data-testid="deactivate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserX className="w-4 h-4"/> Nonaktifkan Pegawai</DialogTitle>
            <DialogDescription>Pegawai tidak akan muncul di selector tugas/booking baru. Semua histori tetap tersimpan dan dapat diaktifkan kembali.</DialogDescription>
          </DialogHeader>
          {deactivateTarget && <div className="p-2 bg-[#FAF6EE] border border-[#E5E0D8] rounded text-sm font-medium">{deactivateTarget.name} — {deactivateTarget.email}</div>}
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Alasan (opsional)</Label>
            <Textarea rows={2} value={deactivateReason} onChange={(e) => setDeactivateReason(e.target.value)} className="mt-1.5" data-testid="deactivate-reason"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)}>Batal</Button>
            <Button onClick={doDeactivate} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="deactivate-confirm-btn">
              {busy ? "Menonaktifkan..." : "Nonaktifkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <ConfirmDeleteModal
        open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Hapus Pegawai Permanen"
        message="Pegawai ini tidak memiliki histori operasional. Aksi akan menghapus data secara permanen (Recycle Bin 30 hari)."
        itemLabel={deleteTarget ? `${deleteTarget.name} — ${deleteTarget.email}` : ""}
        onConfirm={doDelete}
      />

      {/* Force Delete (owner only) */}
      <ForceDeleteModal
        open={!!forceDeleteTarget}
        onOpenChange={(v) => !v && setForceDeleteTarget(null)}
        entityType="employee"
        entityId={forceDeleteTarget?.id}
        entityLabel={forceDeleteTarget ? `${forceDeleteTarget.name} — ${forceDeleteTarget.email}` : ""}
        onDone={() => { setForceDeleteTarget(null); load(); }}
      />

      {/* Delete Conflict */}
      <Dialog open={!!deleteConflict} onOpenChange={(v) => !v && setDeleteConflict(null)}>
        <DialogContent data-testid="emp-delete-conflict-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5"/> Tidak dapat dihapus permanen</DialogTitle>
            <DialogDescription>Pegawai ini memiliki histori dan tidak dapat dihapus permanen. Nonaktifkan saja.</DialogDescription>
          </DialogHeader>
          {deleteConflict && (
            <div className="space-y-3">
              <div className="p-2 bg-[#FAF6EE] border border-[#E5E0D8] rounded text-sm font-medium">{deleteConflict.emp.name}</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(deleteConflict.relations).filter(([k]) => k !== "total").map(([k, v]) => (
                  <div key={k} className="p-2 bg-amber-50 border border-amber-200 rounded">
                    <div className="font-mono font-semibold">{v}</div>
                    <div className="text-[10px] uppercase text-amber-800">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConflict(null)}>Tutup</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="conflict-deactivate-btn"
              onClick={() => { setDeactivateTarget(deleteConflict.emp); setDeleteConflict(null); }}>
              <UserX className="w-3.5 h-3.5 mr-1"/> Nonaktifkan Saja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EField({ label, k, form, setForm, type = "text", placeholder, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5" type={type} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        placeholder={placeholder} disabled={disabled} data-testid={`emp-${k}`} />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[#8C9096]">{label}</div>
      <div className="mt-0.5 text-[#1E2022]">{value || "—"}</div>
    </div>
  );
}

function StatBox({ label, value, tone }) {
  const cls = tone === "success" ? "text-emerald-700" : tone === "danger" ? "text-rose-700" : "text-[#1E2022]";
  return (
    <div className="p-2 bg-[#FAF6EE] rounded border border-[#E5E0D8]">
      <div className={`font-mono font-semibold text-sm ${cls}`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase text-[#8C9096]">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = status || "active";
  const cls = s === "inactive" ? "border-zinc-300 text-zinc-700 bg-zinc-100"
    : s === "pending" ? "border-amber-300 text-amber-800 bg-amber-50"
    : "border-emerald-300 text-emerald-800 bg-emerald-50";
  const label = s === "inactive" ? "Nonaktif" : s === "pending" ? "Menunggu" : "Aktif";
  return <Badge variant="outline" className={`text-[10px] uppercase ${cls}`}>{label}</Badge>;
}

function EmpActions({ u, me, mgmt, owner, canForce, onView, onEdit, onDeactivate, onActivate, onDelete, onForceDelete }) {
  const isSelf = me?.id === u.id;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`emp-actions-${u.id}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={onView} data-testid={`emp-action-view-${u.id}`}>
          <Eye className="w-3.5 h-3.5 mr-2"/> Lihat
        </DropdownMenuItem>
        {mgmt && (
          <DropdownMenuItem onClick={onEdit} data-testid={`emp-action-edit-${u.id}`}>
            <Pencil className="w-3.5 h-3.5 mr-2"/> Edit
          </DropdownMenuItem>
        )}
        {mgmt && u.status !== "inactive" && !isSelf && (
          <DropdownMenuItem onClick={onDeactivate} data-testid={`emp-action-deactivate-${u.id}`}>
            <UserX className="w-3.5 h-3.5 mr-2"/> Nonaktifkan
          </DropdownMenuItem>
        )}
        {mgmt && u.status === "inactive" && (
          <DropdownMenuItem onClick={onActivate} data-testid={`emp-action-activate-${u.id}`}>
            <UserCheck className="w-3.5 h-3.5 mr-2"/> Aktifkan Kembali
          </DropdownMenuItem>
        )}
        {owner && !isSelf && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-rose-700 focus:text-rose-800 focus:bg-rose-50" data-testid={`emp-action-delete-${u.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-2"/> Hapus Permanen
            </DropdownMenuItem>
          </>
        )}
        {canForce && !isSelf && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onForceDelete} className="text-rose-900 focus:text-rose-900 focus:bg-rose-100 font-semibold" data-testid={`emp-action-force-delete-${u.id}`}>
              <Flame className="w-3.5 h-3.5 mr-2"/> Force Delete Permanen
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
